import React from "react";
import clonedeep from "lodash.clonedeep";
//
import { useGraphAPIHook } from "../../../portal";
import { useExpertProfileContext } from "../../hooks/expert-profile/ExpertProfileContext";
import { GET_PROFILE_FIELDS, GET_USER_PROFILE_FIELDS, VERIFY_IDENTITY } from "../../apis/onboarding-queries/UserOnboardingVerificationQueries";
import { useNotificationContext } from "@limitless/common/hooks";
import { getBirthMonths, getBirthDays, getBirthYears, getMonthLabel } from "./ExpoertOnbonboardingVerificationHelper";
const DEFAULT_PROFILE_FILEDS = {
  loading: true,
  fields: [],
  countrySubDivisions: [],
  verificationConfigured: false,
  verified: false,
  verificationAttemptRemaining: 0,
  idVerificationUIState: null,
  failedToMatch: false,
  isIDServiceDown: false
};
const DEFAULT_PROCESSED_FIELDS = {
  processed: false,
  displayGroups: {
    name: [],
    dob: [],
    otherPersonal: [],
    location: [],
    address: [],
    other: []
  }
};
const DEFAULT_VERIFICATION_DATA = {
  terms: {
    value: false,
    pristine: true,
    required: true
  }
};
export const useExpertOnboardingIdVerification = (t = t => t) => {
  const [profileFields, setProfileFields] = React.useState(DEFAULT_PROFILE_FILEDS);
  const [processedFields, setProcessedFields] = React.useState(DEFAULT_PROCESSED_FIELDS);
  const [verificationData, setVerificationData] = React.useState(DEFAULT_VERIFICATION_DATA);
  const {
    fetchData,
    mutateData
  } = useGraphAPIHook();
  const {
    loadExpertProfile
  } = useExpertProfileContext();
  const {
    addSuccessNotification,
    addFailureNotification
  } = useNotificationContext();
  React.useEffect(() => {
    if (!profileFields.loading && profileFields.fields.length > 0) {
      processProfileFields();
    }
  }, [profileFields.loading]);
  const updateProfile = (result, fromVerify = false) => {
    setProfileFields({
      loading: false,
      fields: result.fields,
      countrySubDivisions: result.countrySubDivisions,
      verificationConfigured: result.verificationConfigured,
      verified: result.verified,
      verificationAttemptRemaining: result.verificationAttemptRemaining,
      idVerificationUIState: result.idVerificationUIState,
      isIDServiceDown: false,
      failedToMatch: result.verificationConfigured && result.idVerificationUIState === false && result.verificationAttemptRemaining > 0,
      outOfTries: result.verificationConfigured && !result.verified && result.verificationAttemptRemaining === 0
    });
    if (fromVerify) {
      if (result.verified) {
        addSuccessNotification("Verification Successful");
      } else {
        addFailureNotification("Sorry, your ID couldn't be verified.");
      }
    }
  };

  // Going to be called from the EWA
  const getExpertProfileFields = () => {
    const options = {
      dataKey: "getProfileFields",
      query: GET_PROFILE_FIELDS,
      functionName: "ExpertOnboardingIdVerificationHook getProfileFields"
    };
    fetchProfileFields(options);
  };

  // Going to be called from the CM
  const getProfileFieldsForUser = userName => {
    const options = {
      dataKey: "getProfileFieldsForUser",
      query: GET_USER_PROFILE_FIELDS,
      variables: {
        userName
      },
      functionName: "ExpertOnboardingIdVerificationHook getProfileFieldsForUser"
    };
    fetchProfileFields(options);
  };
  const fetchProfileFields = options => {
    setProfileFields({
      ...DEFAULT_PROFILE_FILEDS,
      loading: true
    });
    fetchData(options).then(result => updateProfile(result)).catch(() => setProfileFields({
      ...DEFAULT_PROFILE_FILEDS,
      loading: false,
      isIDServiceDown: true
    }));
  };
  const transformVerificationData = () => {
    var _verificationData$ter, _verificationData$ter2;
    const transform = [];
    Object.entries(verificationData).map(([key, field]) => {
      if (key !== "terms" && field.value && field.value !== "") {
        if (key === "dayOfBirth" || key === "monthOfBirth" || key === "yearOfBirth") {
          transform.push({
            name: key,
            value: field.value.value.toString(),
            infoType: field.infoType || ""
          });
        } else {
          transform.push({
            name: key,
            value: field.value.toString(),
            infoType: field.infoType || ""
          });
        }
      }
    });
    return {
      tncAccepted: (_verificationData$ter = (_verificationData$ter2 = verificationData["terms"]) === null || _verificationData$ter2 === void 0 ? void 0 : _verificationData$ter2.value) !== null && _verificationData$ter !== void 0 ? _verificationData$ter : false,
      fields: transform
    };
  };
  const verifyIdentity = () => {
    const options = {
      dataKey: "verifyIdentity",
      mutation: VERIFY_IDENTITY,
      variables: transformVerificationData(),
      functionName: "ExpertOnboardingIdVerificationHook verifyIdentity"
    };
    setVerificationData(DEFAULT_VERIFICATION_DATA);
    setProfileFields(DEFAULT_PROFILE_FILEDS);
    setProcessedFields(DEFAULT_PROCESSED_FIELDS);
    mutateData(options).then(result => {
      updateProfile({
        ...result
      }, true);
      loadExpertProfile();
      if (!profileFields.verificationConfigured) {
        addSuccessNotification("Profile changes successfully saved");
      }
    }).catch(() => setProfileFields({
      ...DEFAULT_PROFILE_FILEDS,
      loading: false,
      isIDServiceDown: true
    }));
  };
  const updateVerificationField = (fieldName, newValue) => {
    const newField = {
      [fieldName]: {
        ...verificationData[fieldName],
        value: newValue,
        pristine: newValue ? false : true
      }
    };
    setVerificationData({
      ...verificationData,
      ...newField
    });
  };
  const processProfileFields = () => {
    const displayGroups = clonedeep(DEFAULT_PROCESSED_FIELDS.displayGroups);
    const fieldData = {
      ...verificationData
    };
    const addToFieldData = field => {
      fieldData[field.name] = {
        value: field.value || "",
        pristine: !field.value && field.required,
        type: field.type,
        infoType: field.infoType,
        required: field.required
      };
    };
    profileFields.fields.map(field => {
      if (field.infoType === "personinfo") {
        if (["firstName", "middleName", "lastName", "MiddleInitial"].includes(field.name)) {
          displayGroups.name.push(field);
          addToFieldData(field);
        } else if (["dayOfBirth", "monthOfBirth", "yearOfBirth"].includes(field.name)) {
          displayGroups.dob.push(field);
          if (field.name === "monthOfBirth") {
            addToFieldData({
              ...field,
              value: field.value ? {
                label: getMonthLabel(field.value, t),
                value: field.value
              } : null
            });
          } else {
            addToFieldData({
              ...field,
              value: field.value ? {
                label: field.value,
                value: field.value
              } : null
            });
          }
        } else {
          displayGroups.otherPersonal.push(field);
          addToFieldData(field);
        }
      } else {
        switch (field.name) {
          case "stateProvinceCode":
            displayGroups.location.push(field);
            break;
          case "address1":
          case "buildingName":
          case "unitNumber":
          case "address2":
          case "streetType":
          case "town":
          case "postalCode":
          case "county":
            displayGroups.address.push(field);
            break;
          default:
            displayGroups.other.push(field);
        }
        addToFieldData(field);
      }
    });
    if (!profileFields.verificationConfigured) {
      Reflect.deleteProperty(fieldData, "terms");
    }
    setVerificationData(fieldData);
    setProcessedFields({
      ...processedFields,
      processed: true,
      displayGroups
    });
  };
  const readOnly = profileFields.verificationConfigured && (profileFields.verified || profileFields.verificationAttemptRemaining < 1);
  return {
    t,
    profileFields,
    processedFields,
    verificationData,
    updateVerificationField,
    getBirthDays,
    getBirthMonths: () => getBirthMonths(t),
    getBirthYears,
    verifyIdentity,
    getExpertProfileFields,
    getProfileFieldsForUser,
    readOnly
  };
};